import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { MenuMore } from '@fs/zion-icon'
import { useLocation, Switch, Route } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { Body2, Button, IconButton, colors, MenuOverlay, ListItem, useAtSize } from '@fs/zion-ui'
import { HelpOverlayButton } from '@fs/zion-help-overlay'
import { boolAttr } from '@fs/zion-frontend-friends'
import sendAnalytics from '../../services/analytics-helper'
import FSLogo from './FSLogo'
import { getExternalBaseUrl } from '../../services/url-service'
import { DataContext, useDataContext } from '../../services/data-service'
import AncestorPageHeader from '../AncestorPage/AncestorPageHeader'
import { ANCESTOR_PAGE_PATHS } from '../../App'

const shadowCss = css`
  position: relative;
  z-index: 2;
  &[sticky] {
    z-index: 2;
    position: sticky;
    top: -60px;
  }
`

const headerCss = css`
  align-items: center;
  background: ${colors.gray00};
  display: flex;
  height: 60px;
  justify-content: space-between;
  padding: 0 24px;

  &[is-mobile] {
    padding: 0 12px;
  }
`

const nameCss = css`
  margin: 13px 5px 0 5px;
`

const navButtonsCss = css`
  display: inline-flex;
  align-items: center;

  a {
    margin-left: 8px;
    display: inline-flex;
    align-items: center;
    height: 42px;
  }
`

export default function AncestorsHeader(): JSX.Element {
  const {
    store: { CF = false },
  } = useDataContext()
  const [t] = useTranslation()
  const user = useUser()
  const pageLocation = useLocation()
  const signedIn = user.signedIn
  const identityUrl = window.location.href.includes('localhost') ? window.location.origin : getExternalBaseUrl()
  // We use useLocation to keep our url state current. window.location.href doesn't stay current.
  const returnUrl = encodeURIComponent(window.location.origin + pageLocation.pathname)

  const atSize = useAtSize()
  const isMobile = atSize({ default: true, md: false })
  const headerButtons = atSize({
    default: (
      <MenuOverlay menuButton={<IconButton emphasis="low" Icon={MenuMore} />} aria-label="Submenu">
        <ListItem
          primaryText={t('common-ui:sign-in.action')}
          external
          data-testid="go-to-log-in"
          to={`${identityUrl}/auth/familysearch/login?returnUrl=${returnUrl}`}
          linkName="Log In"
        />
        <ListItem
          primaryText={t('ancestors.create-account.action')}
          external
          data-testid="go-to-create-account"
          to={`${identityUrl}/register/?returnUrl=${returnUrl}`}
          linkName="Create Account"
        />
      </MenuOverlay>
    ),
    md: (
      <>
        <Button
          data-testid="go-to-log-in"
          emphasis="low"
          external
          to={`${identityUrl}/auth/familysearch/login?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Log In' })
          }}
        >
          {t('common-ui:sign-in.action')}
        </Button>
        <Button
          data-testid="go-to-create-account"
          emphasis="medium"
          external
          to={`${identityUrl}/register/?returnUrl=${returnUrl}`}
          onClick={() => {
            sendAnalytics({ link_page: 'Ancestors', link_name: 'Create Account' })
          }}
        >
          {t('ancestors.create-account.action')}
        </Button>
      </>
    ),
  })

  return (
    <div css={shadowCss}>
      <header css={headerCss} is-mobile={boolAttr(isMobile)}>
        <FSLogo restricted={CF} />
        {!CF && (
          <div css={navButtonsCss}>
            <HelpOverlayButton />
            {!signedIn && !user.userLoading ? (
              headerButtons
            ) : (
              <>
                {!user.userLoading && signedIn && (
                  <>
                    {!isMobile && (
                      <div css={nameCss}>
                        <Body2>{user?.displayName}</Body2>
                      </div>
                    )}
                    <Button
                      data-testid="go-to-sign-out"
                      emphasis="medium"
                      external
                      to={`${identityUrl}/auth/logout/?returnUrl=${returnUrl}`}
                      onClick={() => {
                        sendAnalytics({ link_page: 'Ancestors', link_name: 'Log out' })
                      }}
                    >
                      {t('ancestors.sign-out.action')}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </header>
      <div>
        <Switch>
          <Route exact path={ANCESTOR_PAGE_PATHS}>
            <DataContext>
              <AncestorPageHeader />
            </DataContext>
          </Route>
        </Switch>
      </div>
    </div>
  )
}
